import { batch, createContext, createEffect, createSignal, onCleanup, untrack, useContext } from 'solid-js'
import { AcousticsClient } from './acoustics/client'
import type { AcousticsResult } from './acoustics/compute'
import { AudioEngine } from './audio/engine'
import { ProjectError } from './model/migrate'
import { createPreset, makePrimitive, makeSource, type PresetId } from './model/presets'
import type { Transient } from './model/scene'
import { createSceneStore } from './model/scene'
import { type ClipSettings, type Family, MAX_OBJECTS, MAX_SOURCES, newId, type SceneDoc, type Source, type Vec3 } from './model/schema'
import { addAsset, getAsset, MAX_ASSET_BYTES, memoryAssets, pruneAssets, putAssetMemory } from './persistence/assets'
import { loadAutosave, saveAutosave } from './persistence/autosave'
import { exportProject, importProject, referencedAssetIds } from './persistence/zip'
import { createUiState, type UiSelection } from './ui/state'
import { clampInside, clone, toAcousticScene, track } from './ui/util'
import type { Pose, Selection } from './viewport/types'
import type { Viewport } from './viewport/viewport'

export function defaultClipSettings(assetId: string, fileName: string): ClipSettings {
  return {
    assetId,
    fileName,
    loop: true,
    pitch: 0,
    highpass: 20,
    lowpass: 20000,
    retrigger: { enabled: false, interval: 6, irregularity: 0.6 },
  }
}

export function createApp() {
  const store = createSceneStore(createPreset('starter'))
  const uiState = createUiState()
  const { ui, setUi, toast } = uiState
  const [lastAcoustics, setLastAcoustics] = createSignal<AcousticsResult | null>(null)
  const [ready, setReady] = createSignal(false)
  let engine: AudioEngine | null = null
  let viewport: Viewport | null = null
  let transient: Transient | null = null
  let walkTransient: Transient | null = null
  let fileName = 'megaspace'

  // ---- Acoustic geometry worker --------------------------------------------------------------------
  const client = new AcousticsClient((r) => {
    setLastAcoustics(r)
    engine?.applyAcoustics(r)
  })
  onCleanup(() => {
    client.dispose()
    void engine?.dispose()
  })

  createEffect(() => {
    const d = store.doc
    track(d.enclosure)
    track(d.objects)
    track(d.listener.position)
    for (const s of d.sources) {
      track(s.id)
      track(s.position)
    }
    client.request(toAcousticScene(store.plain()))
  })

  // ---- Audio engine sync ---------------------------------------------------------------------------
  createEffect(() => {
    track(store.doc.sources)
    if (ui.audioStatus !== 'on' || !engine) return
    const sources = clone(store.plain().sources)
    void ensureClips(sources).then(() => engine?.syncSources(sources))
    engine.syncSources(sources)
  })
  createEffect(() => {
    const l = store.doc.listener
    const pose = { position: [...l.position] as Vec3, yaw: l.yaw, pitch: l.pitch }
    if (ui.audioStatus === 'on') engine?.setListener(pose)
  })
  createEffect(() => {
    const g = store.doc.settings.masterGain
    if (ui.audioStatus === 'on') engine?.setMasterGain(g)
  })
  createEffect(() => {
    const layer = ui.layerSolo
    if (ui.audioStatus === 'on') engine?.setLayerSolo(layer)
  })

  async function ensureClips(sources: Source[]) {
    if (!engine) return
    for (const s of sources) {
      const id = s.clip?.assetId
      if (!id || engine.hasClip(id)) continue
      const rec = await getAsset(id)
      if (!rec) continue
      try {
        await engine.loadClip(id, rec.bytes)
      } catch {
        toast(`Could not decode "${rec.fileName}".`, 'error')
      }
    }
  }

  const meter = setInterval(() => {
    if (engine && ui.audioStatus === 'on') setUi('level', engine.level())
  }, 90)
  onCleanup(() => clearInterval(meter))

  async function startAudio() {
    if (ui.audioStatus === 'on' || ui.audioStatus === 'starting') return
    setUi({ audioStatus: 'starting', audioError: '' })
    try {
      const AC = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      const ctx = new AC({ latencyHint: 'interactive' })
      const eng = await AudioEngine.create(ctx, { room: lastAcoustics()?.room })
      await eng.resume()
      engine = eng
      const doc = store.plain()
      const sources = clone(doc.sources)
      await ensureClips(sources)
      eng.setMasterGain(doc.settings.masterGain)
      eng.setLayerSolo(ui.layerSolo)
      eng.setListener(doc.listener)
      eng.syncSources(sources)
      const r = lastAcoustics()
      if (r) eng.applyAcoustics(r)
      setUi('audioStatus', 'on')
    } catch (err) {
      setUi({ audioStatus: 'error', audioError: err instanceof Error ? err.message : String(err) })
      toast(`Audio failed to start: ${err instanceof Error ? err.message : err}`, 'error')
    }
  }

  async function stopAudio() {
    const e = engine
    engine = null
    setUi({ audioStatus: 'off', level: 0 })
    await e?.dispose()
  }

  // ---- Selection -----------------------------------------------------------------------------------
  function select(sel: UiSelection) {
    setUi('selection', sel)
    if (sel?.kind === 'source') setUi('clickSource', sel.id)
  }

  const selectedObject = () => {
    const s = ui.selection
    return s?.kind === 'object' ? store.doc.objects.find((o) => o.id === s.id) : undefined
  }
  const selectedSource = () => {
    const s = ui.selection
    return s?.kind === 'source' ? store.doc.sources.find((o) => o.id === s.id) : undefined
  }

  // ---- Editing actions -----------------------------------------------------------------------------
  /** A spot in front of the listener, inside the room, at roughly floor/mid height. */
  function spotAhead(distance: number, y?: number): Vec3 {
    const l = store.doc.listener
    const p: Vec3 = [l.position[0] - Math.sin(l.yaw) * distance, y ?? l.position[1], l.position[2] - Math.cos(l.yaw) * distance]
    return clampInside(store.plain(), p, 0.3)
  }

  function addPrimitive(kind: 'box' | 'cylinder' | 'tube' | 'prism') {
    if (store.doc.objects.length >= MAX_OBJECTS) return toast(`Limit of ${MAX_OBJECTS} primitives reached.`, 'error')
    const size: Vec3 = kind === 'box' ? [4, 3, 4] : kind === 'tube' ? [6, 12, 6] : kind === 'prism' ? [10, 8, 10] : [2, 6, 2]
    const p = spotAhead(8, 0)
    const obj = makePrimitive(kind, [snap(p[0]), size[1] / 2, snap(p[2])], size)
    obj.name = `${kind === 'box' ? 'Block' : kind === 'tube' ? 'Tube' : kind === 'prism' ? 'Shape' : 'Pillar'} ${store.doc.objects.length + 1}`
    if (store.addObject(obj)) select({ kind: 'object', id: obj.id })
  }

  const snap = (v: number) =>
    store.doc.settings.snapEnabled ? Math.round(v / store.doc.settings.snapStep) * store.doc.settings.snapStep : v

  function addSource(family: Family) {
    if (store.doc.sources.length >= MAX_SOURCES) return toast(`Up to ${MAX_SOURCES} sources can play at once.`, 'error')
    const src = makeSource(family, spotAhead(10, family === 'drip' ? 0.4 : 2))
    if (store.addSource(src)) select({ kind: 'source', id: src.id })
  }

  async function importClip(file: File, at?: Vec3) {
    if (store.doc.sources.length >= MAX_SOURCES) return toast(`Up to ${MAX_SOURCES} sources can play at once.`, 'error')
    if (file.size > MAX_ASSET_BYTES) return toast(`"${file.name}" is larger than ${MAX_ASSET_BYTES / 1024 / 1024} MB.`, 'error')
    try {
      const bytes = await file.arrayBuffer()
      // Validate with a throwaway offline context so import works before audio is started.
      const probe = new OfflineAudioContext(1, 1, 44100)
      const decoded = await probe.decodeAudioData(bytes.slice(0))
      const id = newId('clip')
      const rec = { fileName: file.name, mime: file.type || 'audio/wav', bytes }
      await addAsset(id, rec)
      const src = makeSource(
        'vent',
        at ? clampInside(store.plain(), at, 0.3) : spotAhead(6, 1.5),
        file.name.replace(/\.[^.]+$/, '').slice(0, 40),
      )
      src.kind = 'clip'
      src.clip = defaultClipSettings(id, file.name)
      src.hfCut = 16000
      if (decoded.numberOfChannels > 1) toast('Stereo clip downmixed to mono for point-source playback.')
      if (store.addSource(src)) select({ kind: 'source', id: src.id })
    } catch {
      toast(`"${file.name}" is not a supported audio file.`, 'error')
    }
  }

  function deleteSelection() {
    const s = ui.selection
    if (!s) return
    if (s.kind === 'object') store.remove('objects', s.id)
    else if (s.kind === 'source') store.remove('sources', s.id)
    else return
    select({ kind: 'room' })
  }

  function duplicateSelection() {
    const s = ui.selection
    if (s?.kind === 'object') {
      const c = store.duplicateObject(s.id)
      if (c) select({ kind: 'object', id: c.id })
      else toast(`Limit of ${MAX_OBJECTS} primitives reached.`, 'error')
    } else if (s?.kind === 'source') {
      const c = store.duplicateSource(s.id)
      if (c) select({ kind: 'source', id: c.id })
      else toast(`Up to ${MAX_SOURCES} sources can play at once.`, 'error')
    }
  }

  function listenHere() {
    if (!viewport) return
    const pose = viewport.getCameraPose()
    const position = clampInside(store.plain(), pose.position, 0.3)
    store.patch({ c: 'listener' }, { position, yaw: pose.yaw, pitch: pose.pitch }, 'Listen here')
    toast('Head moved to the camera.')
  }

  // ---- Viewport event handling ---------------------------------------------------------------------
  const keysFor = (sel: NonNullable<Selection>) =>
    sel.kind === 'object' ? ['position', 'rotation', 'size'] : sel.kind === 'source' ? ['position'] : ['position', 'yaw', 'pitch']
  const selectorFor = (sel: NonNullable<Selection>) =>
    sel.kind === 'object'
      ? ({ c: 'objects', id: sel.id } as const)
      : sel.kind === 'source'
        ? ({ c: 'sources', id: sel.id } as const)
        : ({ c: 'listener' } as const)

  const viewportEvents = {
    pick: (sel: Selection) => select(sel ?? { kind: 'room' }),
    transformStart: (sel: NonNullable<Selection>) => {
      transient = store.transient(selectorFor(sel), keysFor(sel))
    },
    transformChange: (sel: NonNullable<Selection>, pose: Pose & { yaw: number; pitch: number }) => {
      if (!transient) return
      const doc = store.plain()
      if (sel.kind === 'object') {
        transient.preview({ position: pose.position, rotation: pose.rotation, size: pose.size.map((v) => Math.max(0.05, Math.abs(v))) })
      } else if (sel.kind === 'source') {
        transient.preview({ position: clampInside(doc, pose.position) })
      } else {
        transient.preview({ position: clampInside(doc, pose.position), yaw: pose.yaw, pitch: pose.pitch })
      }
    },
    transformEnd: (sel: NonNullable<Selection>) => {
      transient?.commit(sel.kind === 'object' ? 'Transform object' : sel.kind === 'source' ? 'Move source' : 'Move listener')
      transient = null
    },
    walk: (pose: { position: Vec3; yaw: number; pitch: number }) => {
      walkTransient ??= store.transient({ c: 'listener' }, ['position', 'yaw', 'pitch'])
      walkTransient.preview(pose)
    },
    speedChanged: (v: number) => setUi('walkSpeed', v),
    exploreEnded: () => {
      walkTransient?.commit('Walk')
      walkTransient = null
      setUi('mode', 'edit')
    },
  }

  async function toggleExplore() {
    if (!viewport) return
    if (ui.mode === 'explore') {
      viewport.exitExplore()
      return
    }
    const ok = await viewport.enterExplore()
    if (ok) {
      setUi('walkSpeed', viewport.walkSpeed)
      setUi('mode', 'explore')
      if (ui.audioStatus !== 'on') toast('Explore mode is silent until you press Start Audio.')
    } else toast('The browser refused pointer lock. Click the viewport and try again.', 'error')
  }

  // ---- Click test ----------------------------------------------------------------------------------
  function clickTest() {
    if (ui.audioStatus !== 'on' || !engine) return toast('Press Start Audio first.', 'error')
    const sel = ui.selection
    const id = sel?.kind === 'source' ? sel.id : (ui.clickSource ?? store.doc.sources[0]?.id)
    if (!id || !store.doc.sources.some((s) => s.id === id))
      return toast('Add a source first; the click plays from a source position.', 'error')
    engine.clickTest(id)
  }

  // ---- Projects ------------------------------------------------------------------------------------
  function loadDoc(doc: SceneDoc, name?: string) {
    batch(() => {
      store.replace(doc)
      select({ kind: 'room' })
      setUi({ mode: 'edit' })
    })
    if (name) fileName = name
    viewport?.frameScene()
  }

  function newFromPreset(id: PresetId) {
    const doc = createPreset(id)
    fileName = doc.name
    loadDoc(doc)
    void pruneAssets(new Set(), [...memoryAssets().keys()])
  }

  function saveZip() {
    const doc = store.plain()
    const bytes = exportProject(doc, memoryAssets())
    const blob = new Blob([bytes as Uint8Array<ArrayBuffer>], { type: 'application/zip' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${(doc.name || 'megaspace').replace(/[^\w.-]+/g, '-')}.megaspace.zip`
    a.click()
    setTimeout(() => URL.revokeObjectURL(url), 2000)
    toast('Project saved.')
  }

  async function openZip(file: File) {
    try {
      const imported = importProject(new Uint8Array(await file.arrayBuffer()))
      for (const [id, rec] of imported.assets) await addAsset(id, rec)
      loadDoc(imported.doc, imported.doc.name)
      if (imported.missing.length) toast(`${imported.missing.length} audio clip(s) were missing from the project.`, 'error')
      else toast(`Opened "${imported.doc.name}".`)
    } catch (err) {
      toast(err instanceof ProjectError ? err.message : 'Could not open that project.', 'error')
    }
  }

  // ---- Autosave ------------------------------------------------------------------------------------
  let saveTimer: ReturnType<typeof setTimeout> | undefined
  createEffect(() => {
    const rev = store.revision()
    if (rev === 0 || !ready()) return
    clearTimeout(saveTimer)
    saveTimer = setTimeout(() => {
      const doc = untrack(() => clone(store.plain()))
      const ids = referencedAssetIds(doc)
      const meta = [...ids].flatMap((id) => {
        const rec = memoryAssets().get(id)
        return rec ? [{ id, fileName: rec.fileName, path: '', mime: rec.mime }] : []
      })
      void saveAutosave(doc, meta)
    }, 700)
  })
  onCleanup(() => clearTimeout(saveTimer))

  async function restore() {
    const doc = await loadAutosave()
    if (doc) {
      store.replace(doc)
      fileName = doc.name
      const ids = referencedAssetIds(doc)
      await Promise.all([...ids].map(async (id) => putIfPresent(id)))
    }
    setReady(true)
  }
  async function putIfPresent(id: string) {
    const rec = await getAsset(id)
    if (rec) putAssetMemory(id, rec)
  }
  void restore()

  return {
    store,
    ...uiState,
    lastAcoustics,
    selectedObject,
    selectedSource,
    select,
    addPrimitive,
    addSource,
    importClip,
    deleteSelection,
    duplicateSelection,
    listenHere,
    toggleExplore,
    clickTest,
    startAudio,
    stopAudio,
    newFromPreset,
    saveZip,
    openZip,
    viewportEvents,
    setViewport: (v: Viewport | null) => {
      viewport = v
    },
    getViewport: () => viewport,
    getEngine: () => engine,
    get fileName() {
      return fileName
    },
    ready,
  }
}

export type App = ReturnType<typeof createApp>

const AppContext = createContext<App>()
export const AppProvider = AppContext.Provider
export function useApp(): App {
  const a = useContext(AppContext)
  if (!a) throw new Error('useApp outside AppProvider')
  return a
}
